import { useMemo } from 'react';

import { useMachine } from '@xstate/react';
import { useRouter } from 'next/navigation';

import { DepthWaveWrapper } from '../../../../components/DepthWaveWrapper/DepthWaveWrapper';
import { Modal } from '../../../../components/Modal/Modal';
import { Redirect } from '../../../../components/Redirect/Redirect';
import { useAnalytics } from '../../../../contexts/AnalyticsContext/AnalyticsContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { reportMykissError } from '../../../../helpers/errorHandling';
import { useCurrentURL } from '../../../../helpers/hooks/useCurrentURL/useCurrentURL';
import { useGiveConsent } from '../../../../helpers/hooks/useGiveConsent';
import type { PickUnionLiterals } from '../../../../interfaces/PickUnionLiterals';
import { URLParameters } from '../../constants';
import { getFlow } from '../../helpers/flow/getFlow';
import { getMachine } from '../../machine/machine';
import type { States } from '../../machine/types';

interface StandaloneAuthenticationProps {
  initialFlow: PickUnionLiterals<States, 'login' | 'signUp'>;
}

export const StandaloneAuthentication = ({ initialFlow }: StandaloneAuthenticationProps) => {
  const { setSignupMethod, getToken, checkForTokens } = useAuth();
  const { replace } = useRouter();
  const { removeParameters } = useCurrentURL();
  const [updateConsent] = useGiveConsent();
  const { currentPage } = useAnalytics();
  const machine = useMemo(() => getMachine(initialFlow), [initialFlow]);
  const [state] = useMachine(machine, {
    input: {
      analytics: { currentPage },
      helpers: {
        checkForTokens,
        getToken,
        setSignupMethod,
        updateConsent,
      },
    },
  });
  const stateName = state.value.toString();
  const Flow = getFlow(stateName);
  const fallbackURL = removeParameters([URLParameters.Flow]);

  const handleClose = async (): Promise<void> => {
    const clearedURL = removeParameters([URLParameters.Flow]);
    try {
      replace(clearedURL);
    } catch (error) {
      reportMykissError(error);
    }
  };

  return (
    <Modal
      className="overflow-y-scroll"
      dataTestId="authentication-modal"
      isOpen
      onClose={handleClose}
      outerClassName="z-modal-auth"
      size="full"
      style={{ minWidth: '100vw' }}
    >
      <DepthWaveWrapper logo>
        {Flow ? <Flow authenticationState={state} /> : <Redirect to={fallbackURL} />}
      </DepthWaveWrapper>
    </Modal>
  );
};
